import { Calendar, CaretUpDown } from "@phosphor-icons/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@reactive-resume/utils";
import dayjs from "dayjs";
import { forwardRef, useState } from "react";

import { Button } from "./button";

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  format?: string;
}

export interface DateRangePickerProps {
  startValue?: string;
  endValue?: string;
  onChange?: (startDate: string, endDate: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  format?: string;
}

// 生成日历网格
const generateCalendar = (year: number, month: number) => {
  const firstDay = dayjs(`${year}-${month + 1}-01`);
  const lastDay = firstDay.endOf('month');
  const startDate = firstDay.startOf('week');
  const endDate = lastDay.endOf('week');
  
  const days = [];
  let current = startDate;
  
  while (current.isBefore(endDate) || current.isSame(endDate)) {
    days.push(current);
    current = current.add(1, 'day');
  }
  
  return days;
};

// 单日期选择器
export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ value, onChange, placeholder = "选择日期", disabled = false, className, format = "YYYY-MM-DD" }, ref) => {
    const [open, setOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(dayjs());
    
    const selectedDate = value ? dayjs(value) : null;
    
    const handleDateSelect = (date: dayjs.Dayjs) => {
      onChange?.(date.format(format));
      setOpen(false);
    };
    
    const handleOpenChange = (newOpen: boolean) => {
      setOpen(newOpen);
    };
    
    const handlePrevMonth = () => {
      console.log('Previous month clicked');
      setCurrentDate(currentDate.subtract(1, 'month'));
    };
    
    const handleNextMonth = () => {
      console.log('Next month clicked');
      setCurrentDate(currentDate.add(1, 'month'));
    };
    
    const calendarDays = generateCalendar(currentDate.year(), currentDate.month());
    
    return (
      <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <Button
            ref={ref}
            variant="outline"
            className={cn(
              "w-full justify-between text-left font-normal",
              !value && "text-muted-foreground",
              className
            )}
            disabled={disabled}
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {selectedDate ? selectedDate.format(format) : placeholder}
            </div>
            <CaretUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className="z-50 w-auto p-0 bg-background border border-border rounded-md shadow-md"
            align="start"
          >
            <div className="p-3">
              {/* 月份导航 */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePrevMonth();
                  }}
                  className="h-7 w-7 p-0"
                >
                  ‹
                </Button>
                <div className="text-sm font-medium">
                  {currentDate.format('YYYY年MM月')}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNextMonth();
                  }}
                  className="h-7 w-7 p-0"
                >
                  ›
                </Button>
              </div>
              
              {/* 星期标题 */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['日', '一', '二', '三', '四', '五', '六'].map((day) => (
                  <div key={day} className="h-8 w-8 text-center text-xs font-medium text-muted-foreground flex items-center justify-center">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* 日期网格 */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((date, index) => {
                  const isCurrentMonth = date.month() === currentDate.month();
                  const isSelected = selectedDate && date.isSame(selectedDate, 'day');
                  const isToday = date.isSame(dayjs(), 'day');
                  
                  return (
                    <Button
                      key={index}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-8 w-8 p-0 text-xs",
                        !isCurrentMonth && "text-muted-foreground opacity-50",
                        isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                        isToday && !isSelected && "bg-accent text-accent-foreground"
                      )}
                      onClick={() => handleDateSelect(date)}
                    >
                      {date.date()}
                    </Button>
                  );
                })}
              </div>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);

DatePicker.displayName = "DatePicker";

// 日期范围选择器
export const DateRangePicker = forwardRef<HTMLButtonElement, DateRangePickerProps>(
  ({ startValue, endValue, onChange, placeholder = "选择日期", disabled = false, className, format = "YYYY-MM-DD" }, ref) => {
    const [open, setOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [selectingStart, setSelectingStart] = useState(true);
    
    // 改进日期解析逻辑，支持多种格式
    const parseDate = (dateStr: string) => {

      console.log('DateRangePicker parseDate input:', dateStr);
      if (!dateStr || !dateStr.trim()) {
        console.log('Empty date string, returning null');
        return null;
      }
      
      const trimmed = dateStr.trim();
      console.log('Parsing date:', trimmed);
      
      // 尝试多种日期格式，使用严格模式
      const formats = ['YYYY-MM-DD', 'YYYY.MM.DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'DD/MM/YYYY'];
      
      for (const fmt of formats) {
        const parsed = dayjs(trimmed, fmt, true); // 第三个参数true表示严格模式
        if (parsed.isValid()) {
          console.log('Successfully parsed with format:', fmt, 'Result:', parsed.format('YYYY-MM-DD'));
          return parsed;
        }
      }
      
      // 如果所有格式都失败，尝试默认解析，但要确保不是空字符串
      if (trimmed.length > 0) {
        const defaultParsed = dayjs(trimmed);
        if (defaultParsed.isValid() && !defaultParsed.isSame(dayjs(), 'day')) {
          // 确保解析的日期不是今天（避免空字符串被解析为今天）
          console.log('Successfully parsed with default format:', defaultParsed.format('YYYY-MM-DD'));
          return defaultParsed;
        }
      }
      
      console.log('Failed to parse date:', trimmed);
      return null;
    };
    
    const startDate = parseDate(startValue || "");
    const endDate = parseDate(endValue || "");
    
    // 当组件打开时，重置选择状态
    const handleOpenChange = (newOpen: boolean) => {
      console.log('Popover open change:', newOpen, 'startDate:', startDate?.format(format) || 'null', 'endDate:', endDate?.format(format) || 'null');
      setOpen(newOpen);
      if (newOpen) {
        // 如果没有开始日期，选择开始日期
        // 如果有开始日期但没有结束日期，选择结束日期
        // 如果两个日期都有，重新选择开始日期
        const shouldSelectStart = !startDate || (startDate && endDate);
        console.log('Setting selectingStart to:', shouldSelectStart);
        setSelectingStart(Boolean(shouldSelectStart));
      }
    };
    
    const handleDateSelect = (date: dayjs.Dayjs) => {
      console.log('Date selected:', date.format(format), 'selectingStart:', selectingStart, 'startDate:', startDate?.format(format) || 'null', 'endDate:', endDate?.format(format) || 'null');
      
      if (!startDate || selectingStart) {
        // 选择开始日期：当没有开始日期或明确要选择开始日期时
        console.log('Setting start date:', date.format(format));
        onChange?.(date.format(format), "");
        setSelectingStart(false);
        // 选择开始日期后不关闭弹窗，等待选择结束日期
        // setOpen(false);
      } else {
        // 选择结束日期：当已有开始日期且不是选择开始日期模式时
        console.log('Setting end date:', date.format(format));
        if (date.isBefore(startDate)) {
          // 如果选择的日期早于开始日期，交换它们
          console.log('Swapping dates');
          onChange?.(date.format(format), startDate.format(format));
        } else {
          // 正常情况，设置结束日期
          console.log('Normal end date setting');
          onChange?.(startValue || "", date.format(format));
        }
        setOpen(false);
        setSelectingStart(true);
      }
    };
    
    const handlePrevMonth = () => {
      setCurrentDate(currentDate.subtract(1, 'month'));
    };
    
    const handleNextMonth = () => {
      setCurrentDate(currentDate.add(1, 'month'));
    };
    
    const calendarDays = generateCalendar(currentDate.year(), currentDate.month());
    
    const displayText = () => {
      console.log('displayText called - startDate:', startDate?.format(format) || 'null', 'endDate:', endDate?.format(format) || 'null');
      
      if (startDate && endDate) {
        const result = `${startDate.format(format)} - ${endDate.format(format)}`;
        console.log('Displaying range:', result);
        return result;
      }
      
      if (startDate) {
        const result = startDate.format(format);
        console.log('Displaying single date:', result);
        return result;
      }
      
      console.log('Displaying placeholder:', placeholder);
      return placeholder;
    };
    
    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <Button
            ref={ref}
            variant="outline"
            className={cn(
              "w-full justify-between text-left font-normal",
              !startValue && !endValue && "text-muted-foreground",
              className
            )}
            disabled={disabled}
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {displayText()}
            </div>
            <CaretUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className="z-50 w-auto p-0 bg-background border border-border rounded-md shadow-md"
            align="start"
          >
            <div className="p-3">
              {/* 月份导航 */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevMonth}
                  className="h-7 w-7 p-0"
                >
                  ‹
                </Button>
                <div className="text-sm font-medium">
                  {currentDate.format('YYYY年MM月')}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextMonth}
                  className="h-7 w-7 p-0"
                >
                  ›
                </Button>
              </div>
              
              {/* 星期标题 */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['日', '一', '二', '三', '四', '五', '六'].map((day) => (
                  <div key={day} className="h-8 w-8 text-center text-xs font-medium text-muted-foreground flex items-center justify-center">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* 日期网格 */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((date, index) => {
                  const isCurrentMonth = date.month() === currentDate.month();
                  const isStartSelected = startDate && date.isSame(startDate, 'day');
                  const isEndSelected = endDate && date.isSame(endDate, 'day');
                  const isInRange = startDate && endDate && date.isAfter(startDate) && date.isBefore(endDate);
                  const isToday = date.isSame(dayjs(), 'day');
                  
                  return (
                    <Button
                      key={index}
                      variant="ghost"
                      size="sm"
                      type="button"
                      className={cn(
                        "h-8 w-8 p-0 text-xs",
                        !isCurrentMonth && "text-muted-foreground opacity-50",
                        (isStartSelected || isEndSelected) && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                        isInRange && "bg-primary/20 hover:bg-primary/30",
                        isToday && !isStartSelected && !isEndSelected && "bg-accent text-accent-foreground"
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Button clicked for date:', date.format(format));
                        handleDateSelect(date);
                      }}
                    >
                      {date.date()}
                    </Button>
                  );
                })}
              </div>
              
              {/* 提示文本和操作按钮 */}
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {selectingStart ? "选择开始日期" : "选择结束日期"}
                </div>
                <div className="flex gap-2">
                  {startDate && !selectingStart && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setOpen(false);
                        setSelectingStart(true);
                      }}
                      className="text-xs h-6"
                    >
                      完成
                    </Button>
                  )}
                  {(startDate || endDate) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onChange?.("", "");
                        setSelectingStart(true);
                      }}
                      className="text-xs h-6"
                    >
                      清除
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);

DateRangePicker.displayName = "DateRangePicker";